// Core Modules
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

// External Modules
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Tokens
import { WINDOW } from '@core/injection-tokens/tokens';

// Enums
import { LocalStorageEnum } from '@shared/enum/local-storage-keys.enum';
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

@Injectable({
  providedIn: 'root',
})
export class StorageTrackerService implements OnDestroy {
  // Injectors
  private readonly ngbModal = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly window = inject(WINDOW);

  constructor() {
    this.stop();
    this.start();
  }

  private start(): void {
    this.window.addEventListener('storage', this.storageEventListener.bind(this));
  }

  private storageEventListener(event: StorageEvent): void {
    // The API reference is public and reads on its own, so a client change or a
    // sign out in another tab must not reload it or send it to login - the reader
    // would lose their place in a document they can see either way.
    if (this.isDocsRoute()) return;

    // Reload the page on below conditions for multiple tabs open
    // 1. Reload on change in user_profile value in local storage
    // 2. Reload the page on clear of local storage
    if (event.storageArea === localStorage && this.window.location.href?.split('/').pop() !== 'tableau.html') {
      if (event.key === null && event.newValue === null) {
        this.ngbModal.dismissAll('Logged out');
        this.router.navigate([`/${MainNavigationEnum.LOGIN}`], {
          onSameUrlNavigation: 'reload',
        });
      }
      if (event.key === LocalStorageEnum.USER_PROFILE && event.newValue && JSON.parse(event.newValue)[UserProfileFieldsEnum.HG_CUSTOMER]) this.window.location.reload();
    }
  }

  /**
   * Whether the reader is on the API reference, matched on the path alone - the
   * reference rewrites the fragment as the reader moves through it.
   * @returns true when the current route is the docs route
   */
  private isDocsRoute(): boolean {
    return this.router.url.split(/[?#]/)[0] === `/${MainNavigationEnum.DOCS}`;
  }

  public stop(): void {
    this.window.removeEventListener('storage', this.storageEventListener.bind(this));
  }

  public ngOnDestroy(): void {
    this.stop();
  }
}
